import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTileData } from '../../../hooks/useTileData';
import {
  getMyTimesheet, logEffort, correctEffort, deleteEffort, restoreEffort, getEffortMembers, effortWriteMessage,
} from '../../../api/hub/effort';

import { useTimer } from '../../../contexts/TimerContext';
import EffortHeader from './EffortHeader';
import RunningTimerHero from './RunningTimerHero';
import DayBars from './DayBars';
import PanelHeading from './PanelHeading';
import EffortTotals from './EffortTotals';
import Timesheet from './Timesheet';
import LogEffortDialog from './LogEffortDialog';
import { SkeletonDashboard, RefetchBar, ErrorState, NotPermitted, ErrorBoundary } from './vendor';
import EffortUndoToast from './EffortUndoToast';
import { isEffortMoveEnabled, isEffortDeleteEnabled, isEffortUndoEnabled } from './effortCapabilities';
import { addedIntervals, checkMove, intervalOf, neighbourWindow, timersHoldingTime } from './entryOverlap';
import { floorToMinute } from './entryTime';
import useNeighbourEntries from './useNeighbourEntries';
import useProjectOptions from './useProjectOptions';

const DEFAULT_RANGE_DAYS = 14;

/** Shown when the days next to a move target could not be loaded to check it. */
const OVERLAP_CHECK_FAILED = "Couldn't check for overlaps. Try again.";

// YYYY-MM-DD (local) for the RPC's p_from / p_to date params.
const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Whether a fetch error is the "you lack the grant" case rather than a real
 * failure. Viewing another person's timesheet without `effort.read.all` makes
 * fn_my_timesheet raise; we render <NotPermitted> (distinct from empty) only
 * for that. The exact Postgres wording/code is UNVERIFIED here (no live DB), so
 * this matches on the usual insufficient-privilege signals AND is only ever
 * consulted when a non-self member is selected.
 */
const isPermissionError = (message) =>
  /permission|denied|not authoriz|not permitted|forbidden|effort\.read|42501/i.test(
    String(message || ''),
  );

/**
 * The "your account isn't wired into the effort hub yet" case. fn_my_timesheet
 * raises `no current team member` (Postgres P0001) whenever the caller's session
 * maps to no ops_effort team member — the expected state until staff accounts are
 * provisioned and the `effort.read.all` grant is wired to the FE. This is NOT a
 * load failure: retrying cannot help, so it renders <NotPermitted> for the caller's
 * own timesheet as well as for a viewed member, rather than the alarming generic
 * error card. Matched on the message text because useTileData keeps `e.message` and
 * drops the Postgres code.
 */
const isNoTeamMember = (message) =>
  /no current team member|no team member|not a team member/i.test(String(message || ''));

/**
 * The Effort page's content — everything inside the page shell (header controls,
 * running-timer hero, day bars, totals, timesheet, and the log/correct dialogs).
 * Rendered by BOTH shells: the customer-dashboard chrome (EffortPage) and the
 * ClientHub HubShell (EffortHubPage), so the same time-tracking surface lives in
 * both places without duplicating the logic.
 */
export default function EffortContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rangeDays = Number(searchParams.get('range')) || DEFAULT_RANGE_DAYS;
  const memberId = searchParams.get('member') || null; // null = self ("Me")
  const viewingOther = !!memberId;

  const updateParam = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null || value === '') next.delete(key);
      else next.set(key, String(value));
      return next;
    });
  }, [setSearchParams]);

  const { from, to } = useMemo(() => {
    const end = new Date(Date.now());
    const start = new Date(Date.now());
    start.setDate(end.getDate() - (rangeDays - 1)); // inclusive window
    return { from: isoDate(start), to: isoDate(end) };
  }, [rangeDays]);

  const { data, loading, error, refresh } = useTileData(
    () => getMyTimesheet(from, to, memberId).then((r) => r?.data ?? null),
    [from, to, memberId],
  );

  // Feed the DB-running timer into the SHARED shell timer so the Active-timer hero,
  // the shell timer widget, and anything started from a task/thread are one timer —
  // not two disconnected copies. The app-root provider mounts before this fetch, so
  // it can't seed via initialRunning; hydrate reconciles instead of clobbering a
  // timer started elsewhere. Only the SELF timesheet owns the shell timer — another
  // person's running timer is not yours to adopt.
  const { hydrate, start, running: shellRunning } = useTimer();
  useEffect(() => {
    if (!viewingOther) hydrate(data?.running || null);
  }, [data?.running, viewingOther, hydrate]);

  // Clients selectable in the "Log time" dialog, derived from the timesheet's
  // own entries — after-the-fact logging is almost always for a client already
  // in view, and this needs no extra RPC. (A full client search can replace this
  // when Charles's client picker lands.)
  const clientOptions = useMemo(() => {
    const seen = new Map();
    for (const e of data?.entries || []) {
      if (e?.client_id && !seen.has(e.client_id)) seen.set(e.client_id, e.client || e.client_id);
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [data]);

  // Whose timesheets the caller may view. fn_effort_members returns just the
  // caller without effort.read.all and the full list with it, so the switcher
  // renders straight from this — no capability branch in the component. Fetched
  // once (range/member independent).
  const { data: memberList } = useTileData(
    () => getEffortMembers().then((r) => r?.data ?? []),
    [],
  );
  const members = memberList || [];

  // The latest payload, for handlers that must read the list as it is when they run
  // rather than as it was when their callback was created.
  const dataRef = useRef(data);
  dataRef.current = data;
  // The shell's running timer. It can be one this list has not been refetched for (started
  // from a task, a thread or the shell widget), and it holds time like the list's own. It is
  // the caller's timer, so it counts only on the caller's own timesheet.
  const shellRunningRef = useRef(null);
  shellRunningRef.current = viewingOther ? null : shellRunning;

  // Days next to a move target, loaded on demand to check an overlap (see handleInlineCorrect).
  const neighbours = useNeighbourEntries({ memberId, from, to });
  const { loadClients, loadTasks } = useProjectOptions();
  const { invalidate: invalidateNeighbours, load: loadNeighbours, checking } = neighbours;

  // A refetch landed: whatever was cached about neighbouring days predates it. Every
  // write on this page (inline, Duplicate, the dialogs, Log, a timer) is followed by a
  // refetch, success or failure, and the rows stay locked until it lands, so this one
  // place drops the cache after every write. A refusal makes no write and no refetch, so
  // the days it loaded stay cached for the next try.
  useEffect(() => {
    invalidateNeighbours();
  }, [data, invalidateNeighbours]);

  const [logOpen, setLogOpen] = useState(false);
  const [correctEntry, setCorrectEntry] = useState(null);
  const [dialogSaving, setDialogSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  // How many writes (an inline save, a duplicate, a dialog save, a logged entry, a timer
  // started from a row) are in flight, counted until their refetch has settled. While it is
  // above zero every row is locked: a second write sent before the list has caught up
  // could target an id the first one superseded, and a slow refetch can land after a fast
  // one and put a superseded row back.
  //
  // A request that never settles leaves every row locked until the page is reloaded
  // (supabase-js sets no request timeout). That is accepted: a hung write is already a
  // broken page, and an unlocked row would be editing a list nobody knows the state of.
  const [writesInFlight, setWritesInFlight] = useState(0);
  // The Undo toast after a delete: `{ kind: 'offer'|'restoring'|'error', id, what, message }`.
  const [undoState, setUndoState] = useState(null);
  // An Undo already sent: a second press before the toast re-renders disabled sends nothing.
  const undoInFlight = useRef(false);
  const writeLocked = writesInFlight > 0;

  /**
   * Runs a write under the page-level gate. The gate is released when `work` settles,
   * which for every caller is after its refetch.
   *
   */
  const trackWrite = useCallback(async (work) => {
    setWritesInFlight((n) => n + 1);
    try {
      return await work();
    } finally {
      setWritesInFlight((n) => n - 1);
    }
  }, []);

  /**
   * Refetches the timesheet and never rejects. `useTileData.refresh` does not reject
   * today, so the catch is a guard against a future hook change, not live handling.
   */
  const refreshQuietly = useCallback(async () => {
    try {
      await refresh();
    } catch {
      // see above: a failed refetch is reported by the page's own error state
    }
  }, [refresh]);

  const handleLog = async ({ minutes, description, when, clientId, service }) => {
    setDialogSaving(true);
    setDialogError(null);
    await trackWrite(async () => {
      try {
        await logEffort({ minutes, description, when, clientId, service });
        setLogOpen(false);
        setDialogSaving(false);
        await refreshQuietly();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('log effort:', e);
        setDialogError(effortWriteMessage(e, 'log effort', 'Could not log time.'));
      } finally {
        setDialogSaving(false);
      }
    });
  };

  // The id of the row the latest correction wrote. After the next refetch it is checked
  // for the `corrected` flag: the timesheet filter drops `corrected: true` entries on the
  // premise that the flag marks the superseded ORIGINAL, which has not been probed. If it
  // turns out to sit on the new row, the filter would hide the edit the user just made.
  const lastCorrectionId = useRef(null);

  // The same for a client change sent without a task: the backend is asked to clear a task
  // that belongs to the previous client (§5f.1), and nothing in the front end can make it.
  // `{ id, previousTaskId }` of the row that write returned.
  const lastClientMove = useRef(null);

  // A different range or person: the entry a pending tripwire is waiting for cannot be in
  // the new list, so stop waiting.
  useEffect(() => {
    lastCorrectionId.current = null;
    lastClientMove.current = null;
  }, [from, to, memberId]);

  /**
   * Whether a change to an entry's time is allowed, as the reason it is not (a message for
   * the row) or `null`.
   *
   * Only a change that ADDS time is checked: a description or project change adds none, and
   * neither does shortening, which is always allowed and needs no request (so it works on
   * an edge day whose neighbours cannot be loaded). The added time is tested for ending in
   * the future and for overlapping the person's other live entries and any running timer
   * (the timesheet's own, and one started from the shell that this list has not caught up
   * with; see checkMove). The loaded timesheet is tried first, which settles a refusal
   * without a request; added time whose neighbouring days are not loaded then loads them. If
   * that load fails the change is refused: an unverified move is never allowed through.
   *
   * @param {object} entry The entry as it is now.
   * @param {{minutes:number, when?:string}} change The new duration and, when the start moved, the new start.
   * @returns {Promise<string|null>}
   */
  const checkTime = useCallback(async (entry, { minutes, when }) => {
    if (when === undefined && minutes === Number(entry.minutes)) return null;
    const start = new Date(when ?? entry.at);
    if (Number.isNaN(start.getTime())) return OVERLAP_CHECK_FAILED;

    const startMs = floorToMinute(start.getTime());
    const added = addedIntervals(intervalOf(entry), { start: startMs, end: startMs + minutes * 60000 });
    if (added.length === 0) return null;

    const now = Date.now();
    // A timer the shell holds counts only until the loaded list has caught up with it.
    const timers = (entries) => timersHoldingTime({
      running: dataRef.current?.running,
      shell: shellRunningRef.current,
      entries,
    });
    const args = { entry, start, minutes, now };
    const loadedEntries = dataRef.current?.entries || [];
    const loaded = checkMove({ ...args, running: timers(loadedEntries), entries: loadedEntries });
    if (loaded) return loaded.message;

    const win = neighbourWindow(
      new Date(Math.min(...added.map((a) => a.start))),
      { from, to, today: isoDate(new Date(now)) },
      new Date(Math.max(...added.map((a) => a.end))),
    );
    if (!win) return null;
    let fetched;
    try {
      fetched = await loadNeighbours(win);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('overlap check:', e);
      return OVERLAP_CHECK_FAILED;
    }
    const ids = new Set(fetched.map((e) => e?.id));
    const entries = [...fetched, ...(dataRef.current?.entries || []).filter((e) => !ids.has(e?.id))];
    return checkMove({ ...args, running: timers(entries), entries })?.message ?? null;
  }, [from, to, loadNeighbours]);

  const handleCorrect = async ({ minutes, description }) => {
    if (!correctEntry) return;
    // The entry may have been replaced since the dialog opened (an earlier correction,
    // or a refetch): correcting the superseded id would write a second live version.
    if (!data) {
      setDialogError("The timesheet couldn't be loaded. Close and try again.");
      return;
    }
    const stillLive = (data.entries || []).some((e) => e?.id === correctEntry.id && e.corrected !== true);
    if (!stillLive) {
      setDialogError('This entry changed. Reopen it.');
      return;
    }
    setDialogSaving(true);
    setDialogError(null);
    await trackWrite(async () => {
      try {
        // An agent entry holds no wall-clock time (it carries a cost), so it is not checked
        // and keeps the dialog's original behaviour.
        if (isEffortMoveEnabled() && correctEntry.actor_type !== 'agent') {
          // The dialog changes minutes too, so it answers to the same overlap and future
          // rules as the row; a refusal is shown in the dialog and nothing is written.
          const refusal = await checkTime(correctEntry, { minutes });
          if (refusal) {
            setDialogError(refusal);
            return;
          }
        }
        lastCorrectionId.current = await correctEffort({ effortId: correctEntry.id, minutes, description });
        setCorrectEntry(null);
        // The dialog is gone; the page gate, not `dialogSaving`, holds the rows until
        // the refetch below has landed.
        setDialogSaving(false);
        await refreshQuietly();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('correct effort:', e);
        setDialogError(effortWriteMessage(e, 'correct effort', 'Could not save the correction.'));
      } finally {
        setDialogSaving(false);
      }
    });
  };

  useEffect(() => {
    const id = lastCorrectionId.current;
    if (!id || !Array.isArray(data?.entries)) return;
    const written = data.entries.find((e) => e?.id === id);
    if (!written) return; // not in this refetch (out of range, or still loading): keep waiting
    lastCorrectionId.current = null;
    if (written.corrected === true) {
      // eslint-disable-next-line no-console
      console.error(
        `Effort: the entry written by the last correction (${id}) came back flagged `
        + '`corrected: true`. The timesheet filter hides such entries, so it is hiding the '
        + 'edit instead of its original: the flag sits on the correction row. Remove the '
        + '`corrected` filter in timesheetGroups.js and ask the backend which row carries it.',
      );
    }
  }, [data]);

  useEffect(() => {
    const move = lastClientMove.current;
    if (!move || !Array.isArray(data?.entries)) return;
    const written = data.entries.find((e) => e?.id === move.id);
    if (!written) return;
    lastClientMove.current = null;
    if (written.task_id === move.previousTaskId) {
      // eslint-disable-next-line no-console
      console.error(
        `Effort: the entry written by a client change (${move.id}) kept the previous client's `
        + 'task. The backend did not clear the task when the client changed; send the explicit '
        + 'clear (p_clear_task) once it is named, and keep the move controls off until then.',
      );
    }
  }, [data]);

  // The account isn't provisioned into the effort hub (P0001 "no current team
  // member"): a known, non-retryable state, shown for self as well as for a viewed
  // member. Checked before the grant case so it wins for either.
  const notProvisioned = Boolean(error) && isNoTeamMember(error);
  const notPermitted = viewingOther && Boolean(error) && isPermissionError(error);

  // A genuine read-RPC failure carries raw Postgres text (fn name, "permission denied
  // for function …"): log it for diagnosis but never render it. The user sees a
  // generic line. `error` itself is still used above to discriminate the NotPermitted
  // paths — that inspects the string, it doesn't display it. The not-permitted and
  // not-provisioned states are expected, not failures, so neither is logged.
  if (error && !notPermitted && !notProvisioned) {
    // eslint-disable-next-line no-console
    console.error('Effort timesheet load failed:', error);
  }

  const openLog = () => { setDialogError(null); setLogOpen(true); };
  const openCorrect = (entry) => { setDialogError(null); setCorrectEntry(entry); };

  // The row's play button starts a fresh timer carrying the entry's what/client/task,
  // then refetches so the new running timer appears in the hero. Its own errors are
  // logged, not surfaced inline — the hero shows the running state, not this row.
  const onStartTimer = useCallback((entry) => trackWrite(async () => {
    try {
      await start({ title: entry?.what, note: entry?.what, client: entry?.client, clientId: entry?.client_id, taskId: entry?.task_id });
      await refreshQuietly();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('start timer from entry:', e);
    }
  }), [start, trackWrite, refreshQuietly]);

  /**
   * A refetch started by the page itself (the hero after a stop, Retry), held under the
   * write gate so rows stay locked until it lands.
   */
  const refreshTracked = useCallback(() => trackWrite(refreshQuietly), [trackWrite, refreshQuietly]);

  /**
   * Duplicates a timesheet entry: logs a copy of it (same description, minutes, start,
   * client, task and service) through fn_effort_log, then refetches so the copy shows.
   *
   * Resolves with `{ status: 'saved' }` or `{ status: 'failed', message }` instead of
   * throwing. The row reads the status to show the message, and tests read it to tell a
   * saved duplicate from a failed one — keep it as the seam even though callers could
   * get by without it.
   *
   * `failed` means the write did not land, as far as the response says. Once
   * fn_effort_log has succeeded the result is `saved` even if the refetch then fails:
   * the copy exists, and a failure status would invite a retry that logs a second one.
   * After a `failed` write the list is refetched too, because a response can be lost
   * after the write committed; the user then sees the copy before retrying.
   *
   * `message` is backend-authored text or the generic line (see effortWriteMessage); the
   * raw error goes to the console.
   *
   * @param {object} entry A fn_my_timesheet entry.
   * @returns {Promise<{status:'saved'}|{status:'failed', message:string}>}
   */
  const handleDuplicate = useCallback((entry) => trackWrite(async () => {
    try {
      await logEffort({
        minutes: entry.minutes,
        description: entry.what,
        when: entry.at,
        clientId: entry.client_id,
        taskId: entry.task_id,
        service: entry.service,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('duplicate effort:', e);
      await refreshQuietly();
      return {
        status: 'failed',
        message: effortWriteMessage(e, 'log effort', 'Could not duplicate this entry.'),
      };
    }
    await refreshQuietly();
    return { status: 'saved' };
  }), [trackWrite, refreshQuietly]);

  /**
   * Saves an inline edit of a timesheet row through fn_effort_correct, then refetches.
   * Same contract as handleDuplicate: it resolves with a status and never throws.
   *
   * The row stays locked until this resolves, which is after the refetch: a correction
   * returns a NEW id, so a second edit sent before the list has caught up would correct
   * the superseded one. `failed` means the write did not land as far as the response
   * says; a refetch failure after it still reads `saved`. After a `failed` write the list
   * is refetched too, because a response can be lost after the write committed.
   *
   * With the move capability on, `change` may also carry `service` (a free-text label) and `when` (the new start),
   * `clientId` and `taskId`, which are sent only when present, and a change of time is
   * checked first (see checkTime). A refused change resolves `failed` with the reason,
   * having written and refetched nothing.
   *
   * @param {object} entry The entry being edited.
   * @param {{minutes:number, description:string, when?:string, clientId?:string, taskId?:string}} change
   *   The full new values, and the move fields that changed.
   * @returns {Promise<{status:'saved'}|{status:'failed', message:string}>}
   */
  const handleInlineCorrect = useCallback((entry, change) => trackWrite(async () => {
    const { minutes, description } = change;
    // The move parameters travel only with the capability on, and only the ones the row
    // sent (the ones the user changed). With it off the call is the original four.
    const moveOn = isEffortMoveEnabled();
    const moveParams = {};
    if (moveOn) {
      if (change.when !== undefined) moveParams.when = change.when;
      if (change.clientId !== undefined) moveParams.clientId = change.clientId;
      if (change.taskId !== undefined) moveParams.taskId = change.taskId;
      if (change.service !== undefined) moveParams.service = change.service;
    }

    if (moveOn) {
      const refusal = await checkTime(entry, change);
      // Nothing was written, so there is nothing to refetch.
      if (refusal) return { status: 'failed', message: refusal };
    }

    try {
      lastCorrectionId.current = await correctEffort({ effortId: entry.id, minutes, description, ...moveParams });
      lastClientMove.current = moveParams.clientId !== undefined && moveParams.taskId === undefined && entry.task_id
        ? { id: lastCorrectionId.current, previousTaskId: entry.task_id }
        : null;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('correct effort:', e);
      await refreshQuietly();
      return {
        status: 'failed',
        message: effortWriteMessage(e, 'correct effort', 'Could not save this change.'),
      };
    }
    await refreshQuietly();
    return { status: 'saved' };
  }), [trackWrite, refreshQuietly, checkTime]);

  /**
   * Deletes a timesheet entry (soft delete through fn_effort_delete), then refetches. Same
   * contract as handleDuplicate: it resolves with a status and never throws.
   *
   * An entry that is no longer among the live loaded entries is refused with no write: its
   * id may already be superseded, and a delete sent for it would remove the wrong version
   * or none. With the undo capability on, `saved` also offers the toast; `saved` is
   * returned once the write resolves even if the refetch fails, because the entry is gone
   * and a retry would only fail. After a `failed` write the list is refetched, since a
   * response can be lost after the write committed.
   *
   * @param {object} entry A fn_my_timesheet entry.
   * @returns {Promise<{status:'saved'}|{status:'failed', message:string}>}
   */
  const handleDelete = useCallback((entry) => trackWrite(async () => {
    if (!isEffortDeleteEnabled()) return { status: 'failed', message: 'Delete is not available.' };
    const live = (dataRef.current?.entries || []).some((e) => e?.id === entry?.id && e.corrected !== true);
    if (!live) return { status: 'failed', message: 'This entry changed. Reload and try again.' };

    try {
      await deleteEffort({ effortId: entry.id });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('delete effort:', e);
      await refreshQuietly();
      return { status: 'failed', message: effortWriteMessage(e, 'delete effort', 'Could not delete this entry.') };
    }
    await refreshQuietly();
    if (isEffortUndoEnabled()) setUndoState({ kind: 'offer', id: entry.id, what: entry.what });
    return { status: 'saved' };
  }), [trackWrite, refreshQuietly]);

  /**
   * Restores the entry just deleted. The restore and its refetch run under the page gate
   * like every other write, so a late refetch cannot put a stale list back over rows the
   * user is already editing. The offer is spent as soon as Undo is pressed.
   */
  const handleUndo = useCallback(() => {
    const offered = undoState;
    if (offered?.kind !== 'offer' || undoInFlight.current) return undefined;
    undoInFlight.current = true;
    setUndoState({ ...offered, kind: 'restoring' });
    return trackWrite(async () => {
      try {
        await restoreEffort({ effortId: offered.id });
        setUndoState(null);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('restore effort:', e);
        setUndoState({
          kind: 'error',
          id: offered.id,
          message: effortWriteMessage(e, 'restore effort', 'Could not restore this entry.'),
        });
      } finally {
        undoInFlight.current = false;
      }
      await refreshQuietly();
    });
  }, [undoState, trackWrite, refreshQuietly]);

  // Which optional panels have something to show. Each panel renders its own
  // heading inside its card; these just gate whether the panel appears at all.
  // `hasTotals` mirrors EffortTotals' own "nothing logged" null check.
  const moveEnabled = isEffortMoveEnabled();
  const today = isoDate(new Date(Date.now()));
  const move = useMemo(() => (moveEnabled
    ? {
      minDate: from,
      maxDate: to < today ? to : today,
      checking,
      loadClients,
      loadTasks,
    }
    : undefined), [moveEnabled, from, to, today, checking, loadClients, loadTasks]);

  const deleteEnabled = isEffortDeleteEnabled();
  const deletion = useMemo(
    () => (deleteEnabled ? { onDelete: handleDelete, undoable: isEffortUndoEnabled() } : undefined),
    [deleteEnabled, handleDelete],
  );
  // Advisory hints are the way into a fix, so they show when either control is on.
  const ownList = data?.is_own ?? !viewingOther;
  const hintContext = (moveEnabled || deleteEnabled) && ownList
    ? { running: data?.running ?? null, shell: shellRunning ?? null, now: Date.now() }
    : undefined;

  const hasDays = data?.days?.length > 0;
  const hasTotals = Boolean(Number(data?.totals?.hours) || Number(data?.agent_totals?.entries));

  return (
    <>
      {/* Full-width content — the page fills the surrounding shell's column. `relative`
          anchors the loader line, which is the column's top edge, above the heading, like a
          top progress bar. It shows on every load: the first one, a control change (member /
          range) refetching behind the stale timesheet, and a write's refetch. */}
      <div className="relative px-4 py-6 md:px-6 lg:px-10">
        {loading ? <RefetchBar /> : null}
        <EffortHeader
          members={members}
          selectedMemberId={memberId}
          onSelectMember={(id) => updateParam('member', id)}
          rangeDays={rangeDays}
          onSelectRange={(days) => updateParam('range', days === DEFAULT_RANGE_DAYS ? null : days)}
          onLog={openLog}
        />

        {/* Panels sit close under the header controls (tight gap), keeping the
            usual rhythm between panels. */}
        <div className="mt-3 space-y-6">
          {loading && !data ? (
            <SkeletonDashboard cards={3} />
          ) : notProvisioned ? (
            <NotPermitted
              title="No effort access yet"
              message="Your account isn't set up for the effort hub. Ask an admin to add you as a team member."
            />
          ) : notPermitted ? (
            <NotPermitted title="Not permitted" message="You don't have access to this person's timesheet." />
          ) : error ? (
            <ErrorState variant="card" message="Couldn't load this timesheet." onRetry={refreshTracked} />
          ) : (
            <>
              <ErrorBoundary title="Running timer">
                <RunningTimerHero onChanged={refreshTracked} />
              </ErrorBoundary>

              {hasDays && (
                <ErrorBoundary title="Daily effort">
                  <div className="bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 p-4 shadow-sm">
                    <PanelHeading
                      title="Daily effort"
                      description="Time logged each day over the range. Amber marks a day that's unusual for you."
                    />
                    <DayBars days={data.days} />
                  </div>
                </ErrorBoundary>
              )}

              {hasTotals && (
                <ErrorBoundary title="Totals">
                  <EffortTotals totals={data?.totals} agentTotals={data?.agent_totals} />
                </ErrorBoundary>
              )}

              <ErrorBoundary title="Timesheet">
                <Timesheet
                  entries={data?.entries || []}
                  onEdit={openCorrect}
                  onStart={onStartTimer}
                  onDuplicate={handleDuplicate}
                  onCorrect={handleInlineCorrect}
                  editable={data?.is_own ?? !viewingOther}
                  locked={writeLocked}
                  move={move}
                  deletion={deletion}
                  hintContext={hintContext}
                />
              </ErrorBoundary>
            </>
          )}
        </div>
      </div>

      {undoState && (
        <EffortUndoToast
          key={undoState.id}
          state={undoState}
          onUndo={handleUndo}
          onDismiss={() => setUndoState(null)}
          disabled={writeLocked}
        />
      )}

      {logOpen && (
        <LogEffortDialog
          mode="log"
          clients={clientOptions}
          onSubmit={handleLog}
          onClose={() => setLogOpen(false)}
          saving={dialogSaving}
          error={dialogError}
        />
      )}
      {correctEntry && (
        <LogEffortDialog
          mode="correct"
          entry={correctEntry}
          onSubmit={handleCorrect}
          onClose={() => setCorrectEntry(null)}
          saving={dialogSaving}
          error={dialogError}
        />
      )}
    </>
  );
}
